import axios from "axios"
import { useState, useEffect } from "react"
import { ChildPracticeMetadata, Groups, PickList, PickListOption } from "../../../types"
import { Box, Button } from "@mui/material"
import constants from "../../../constants"
import GroupTransfer from "./GroupTransfer"
import { useAppDispatch } from "../../../redux/store"
import { getCustomerGroups } from "../../../redux/customer"
import { addError, addSuccess } from "../../../redux/alerts"

type GroupTransferWrapperProps = {
  selectedGroup: Groups | null,
  childPractices: ChildPracticeMetadata[],
  customerNumber: string,
}

const getPickList = (selectedGroup: Groups | null, childPractices: ChildPracticeMetadata[]) => {
  const pickList: PickList = { available: [], members: [] }
  
  if (selectedGroup) {
    const groupMembers = new Set()
    selectedGroup?.members?.forEach(member => groupMembers.add(member.customer_number))
  
    childPractices.forEach(child => {
      const childCustomerNumber = child?.customer_number.trim()

      const option: PickListOption = {
        customerNumber: childCustomerNumber,
        value: `${child?.name.trim()} # ${childCustomerNumber} - ${child?.city.trim()}, ${child?.state.trim()}`,
      }

      if (groupMembers.has(childCustomerNumber)) {
        pickList.members.push(option)
      } else {
        pickList.available.push(option)
      }
    })
  }

  return pickList
}

const getHasMemberChange = (pickList: PickList, original: PickList) => {
  if (original.members.length === pickList.members.length) {
    const memberSet = new Set()
    original.members.forEach(member => memberSet.add(member.customerNumber))

    const hasChange = !pickList.members.every(member => memberSet.has(member.customerNumber))
    return hasChange
  } else {
    return true
  }
}

const GroupTransferWrapper = (props: GroupTransferWrapperProps) => {
  const {
    selectedGroup,
    childPractices,
    customerNumber,
  } = props

  const groupName = selectedGroup?.name ?? ''
  const dispatch = useAppDispatch()

  const [pickList, setPickList] = useState<PickList>({available: [], members: []})
  const [disableSave, setDisableSave] = useState(true)

  useEffect(() => {
    const pickList = getPickList(selectedGroup, childPractices)
    setPickList(pickList)
  }, [selectedGroup, childPractices])

  useEffect(() => {
    if (pickList?.members?.length || pickList?.available?.length) {
      const original = getPickList(selectedGroup, childPractices)
      const hasMemberChange = getHasMemberChange(pickList, original)
      const disableSave = hasMemberChange ? false : true

      setDisableSave(disableSave)
    }
  }, [pickList])

  const resetHandler = () => {
    const original = getPickList(selectedGroup, childPractices)
    setPickList(original)
  }

  const saveHandler = async () => {
    setDisableSave(true)

    const groupId = selectedGroup?.group_id

    const members = pickList.members.map(option => {
      return {
        customer_number: option.customerNumber,
      }
    })

    try {
      await axios.put(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/group-members`, {
        group_id: groupId,
        members,
      })
      dispatch(getCustomerGroups(customerNumber))
      dispatch(addSuccess('Group members updated'))
    } catch (err) {
      setDisableSave(false)
      dispatch(addError('There was a problem updating the group members'))
    }
  }

  return (
    <Box sx={{ flexBasis: '100%' }} className="m-t-16">
      <GroupTransfer
        pickList={pickList}
        setPickList={setPickList}
        groupName={groupName}
      />
      <Box sx={{ color: '#BFC6C8' }} className="f-s-13 m-t-16">
        Place practices in the selected group by selecting them from the "available" list and using the blue arrow buttons.
      </Box>
      <Box sx={{ width: '100%' }} className="flex flex-end m-t-16">
        <Button onClick={resetHandler}>Reset</Button>
        <Button className="m-l-16" variant="contained" onClick={saveHandler} disabled={disableSave}>Save</Button>
      </Box>
    </Box>
  )
}

export default GroupTransferWrapper
